import React, { useState, useRef, useEffect } from 'react';
import { Camera, Upload, Lock, Unlock, RotateCcw, Send, CheckCircle2, Info, User, CreditCard, FileText, Shield, Loader2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import Container from '../common/Container';
import Card from '../common/Card';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import ImageCropper from './ImageCropper'; // 👈 ایمپورت کامپوننت جدید

// Default regions for passport scanning
const PADDING = 0.02;
const MRZ_HEIGHT = 0.20;
const UPPER_HEIGHT = 0.75 - (PADDING * 2);
const PHOTO_WIDTH = (1 / 3) - (PADDING * 2);
const DOCUMENT_WIDTH = (2 / 3) - (PADDING * 1.5);
const GAP = 0.01;
const API_KEY = process.env.REACT_APP_API_KEY;
const INITIAL_REGIONS = {
  photo: { 
    x: PADDING, 
    y: PADDING, 
    w: PHOTO_WIDTH, 
    h: UPPER_HEIGHT, 
    color: 'rgba(220, 38, 38, 0.7)', 
    label: { en: 'Photo', fa: 'عکس' }
  },
  document: { 
    x: PHOTO_WIDTH + PADDING + GAP, 
    y: PADDING, 
    w: DOCUMENT_WIDTH, 
    h: UPPER_HEIGHT, 
    color: 'rgba(0, 138, 94, 0.7)', 
    label: { en: 'Document', fa: 'مدرک' }
  },
  mrz: { 
    x: PADDING, 
    y: 0.75, 
    w: 1 - (PADDING * 3), 
    h: MRZ_HEIGHT - PADDING, 
    color: 'rgba(59, 130, 246, 0.7)', 
    label: { en: 'MRZ', fa: 'MRZ' }
  }
};

const DraggableBox = ({ id, label, color, region, containerRef, onUpdate, visible, enabled, lang }) => {
  const boxRef = useRef(null);
  const stateRef = useRef({
    isDragging: false,
    isResizing: false,
    resizeCorner: null,
    startX: 0,
    startY: 0,
    startRegion: { x: 0, y: 0, w: 0, h: 0 }
  });
  
  // Keep region in ref to access latest value in event handlers
  const regionRef = useRef(region);
  const onUpdateRef = useRef(onUpdate);
  const containerRefRef = useRef(containerRef);
  
  useEffect(() => {
    regionRef.current = region;
  }, [region]);
  
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);
  
  useEffect(() => {
    containerRefRef.current = containerRef;
  }, [containerRef]);

  useEffect(() => {
    if (!enabled) return;

    const getEventPos = (e) => {
      if (e.touches && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    };

    const handleMove = (e) => {
      const state = stateRef.current;
      if (!state.isDragging && !state.isResizing) return;
      
      const container = containerRefRef.current?.current;
      if (!container) return;
      
      e.preventDefault();
      
      const containerRect = container.getBoundingClientRect();
      const pos = getEventPos(e);
      
      const deltaX = (pos.x - state.startX) / containerRect.width;
      const deltaY = (pos.y - state.startY) / containerRect.height;
      
      let newRegion = { ...state.startRegion };
      
      if (state.isDragging) {
        newRegion.x = Math.max(0, Math.min(1 - newRegion.w, state.startRegion.x + deltaX));
        newRegion.y = Math.max(0, Math.min(1 - newRegion.h, state.startRegion.y + deltaY));
      } else if (state.isResizing) {
        const corner = state.resizeCorner;
        const minSize = 0.05;
        
        if (corner.includes('left')) {
          const newX = state.startRegion.x + deltaX;
          const newW = state.startRegion.w - deltaX;
          if (newW >= minSize && newX >= 0) {
            newRegion.x = newX;
            newRegion.w = newW;
          }
        }
        if (corner.includes('right')) {
          const newW = state.startRegion.w + deltaX;
          if (newW >= minSize && state.startRegion.x + newW <= 1) {
            newRegion.w = newW;
          }
        }
        if (corner.includes('top')) {
          const newY = state.startRegion.y + deltaY;
          const newH = state.startRegion.h - deltaY;
          if (newH >= minSize && newY >= 0) {
            newRegion.y = newY;
            newRegion.h = newH;
          }
        }
        if (corner.includes('bottom')) {
          const newH = state.startRegion.h + deltaY;
          if (newH >= minSize && state.startRegion.y + newH <= 1) {
            newRegion.h = newH;
          }
        }
      }
      
      onUpdateRef.current(newRegion);
    };

    const handleEnd = () => {
      stateRef.current.isDragging = false;
      stateRef.current.isResizing = false;
      stateRef.current.resizeCorner = null;
    };

    // Add listeners to document
    document.addEventListener('mousemove', handleMove, { passive: false });
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
    document.addEventListener('touchcancel', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('touchcancel', handleEnd);
    };
  }, [enabled]); // فقط وقتی enabled تغییر کنه

  const handleStart = (e, corner = null) => {
    if (!enabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const pos = e.touches ? 
      { x: e.touches[0].clientX, y: e.touches[0].clientY } : 
      { x: e.clientX, y: e.clientY };
    
    stateRef.current.startX = pos.x;
    stateRef.current.startY = pos.y;
    stateRef.current.startRegion = { ...regionRef.current };
    
    if (corner) {
      stateRef.current.isResizing = true;
      stateRef.current.resizeCorner = corner;
    } else {
      stateRef.current.isDragging = true;
    }
  };

  if (!visible) return null;

  const borderColor = color.replace('0.7', '1');
  const bgColor = color.replace('0.7', '0.9');

  const handleStyle = {
    position: 'absolute',
    width: '18px',
    height: '18px',
    background: '#008a5e',
    border: '2px solid #fff',
    borderRadius: '50%',
    touchAction: 'none',
    zIndex: 10,
  };

  return (
    <div
      ref={boxRef}
      id={id}
      style={{
        position: 'absolute',
        left: `${region.x * 100}%`,
        top: `${region.y * 100}%`,
        width: `${region.w * 100}%`,
        height: `${region.h * 100}%`,
        border: `2px dashed ${borderColor}`,
        boxSizing: 'border-box',
        background: 'transparent',
        touchAction: 'none',
        cursor: enabled ? 'move' : 'default',
        pointerEvents: enabled ? 'auto' : 'none',
        borderRadius: '4px',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
      onMouseDown={(e) => handleStart(e)}
      onTouchStart={(e) => handleStart(e)}
    >
      {/* Label */}
      <div
        style={{
          position: 'absolute',
          top: '6px',
          left: '6px',
          padding: '3px 8px',
          color: '#fff',
          fontSize: '10px',
          fontWeight: 'bold',
          borderRadius: '4px',
          pointerEvents: 'none',
          background: bgColor,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {typeof label === 'object' ? label[lang] : label}
      </div>

      {/* Corner handles */}
      {enabled && (
        <>
          <div
            style={{ ...handleStyle, top: '-9px', left: '-9px', cursor: 'nw-resize' }}
            onMouseDown={(e) => { e.stopPropagation(); handleStart(e, 'top-left'); }}
            onTouchStart={(e) => { e.stopPropagation(); handleStart(e, 'top-left'); }}
          />
          <div
            style={{ ...handleStyle, top: '-9px', right: '-9px', cursor: 'ne-resize' }}
            onMouseDown={(e) => { e.stopPropagation(); handleStart(e, 'top-right'); }}
            onTouchStart={(e) => { e.stopPropagation(); handleStart(e, 'top-right'); }}
          />
          <div
            style={{ ...handleStyle, bottom: '-9px', left: '-9px', cursor: 'sw-resize' }}
            onMouseDown={(e) => { e.stopPropagation(); handleStart(e, 'bottom-left'); }}
            onTouchStart={(e) => { e.stopPropagation(); handleStart(e, 'bottom-left'); }}
          />
          <div
            style={{ ...handleStyle, bottom: '-9px', right: '-9px', cursor: 'se-resize' }}
            onMouseDown={(e) => { e.stopPropagation(); handleStart(e, 'bottom-right'); }}
            onTouchStart={(e) => { e.stopPropagation(); handleStart(e, 'bottom-right'); }}
          />
        </>
      )}
    </div>
  );
};

function PassportCapture({ translations, sessionId, userId, onComplete, mode }) {
  const { t, lang } = useAppContext();
  const [status, setStatus] = useState({ type: 'info', message: '' });
  const getInitialRegions = () => {
    const baseRegions = {
      photo: INITIAL_REGIONS.photo,
      document: INITIAL_REGIONS.document
    };
    if (mode === 'automatic') {
      baseRegions.mrz = INITIAL_REGIONS.mrz;
    }
    return baseRegions;
  };
  const [regions, setRegions] = useState(getInitialRegions);
  const [capturedRegions, setCapturedRegions] = useState(null);
  const [capturedFullImage, setCapturedFullImage] = useState(null);
  const [uploadMode, setUploadMode] = useState(false);
  const [uploadedImageSrc, setUploadedImageSrc] = useState(null);
  const [modifyMode, setModifyMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 👇 state های جدید برای cropper
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageForCrop, setTempImageForCrop] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const containerRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadedImageRef = useRef(null);
  const previewImageRef = useRef(null);

  const trans = translations?.[lang || 'en'] || translations?.en || {};

  useEffect(() => {
    initCamera();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    };
  }, []);

  const initCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', 
          width: { ideal: 1920 }, 
          height: { ideal: 1080 } 
        } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setStatus({ type: 'success', message: lang === 'fa' ? 'دوربین آماده است' : 'Camera ready' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: lang === 'fa' ? `خطای دوربین: ${err.message}` : `Camera error: ${err.message}` });
    }
  };

  const drawGuide = () => {
    const canvas = overlayRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    const updateOverlaySize = () => {
      if (overlayRef.current && containerRef.current) {
        overlayRef.current.width = containerRef.current.offsetWidth;
        overlayRef.current.height = containerRef.current.offsetHeight;
        drawGuide();
      }
    };
    window.addEventListener('resize', updateOverlaySize);
    return () => window.removeEventListener('resize', updateOverlaySize);
  }, [regions]);

  useEffect(() => {
    drawGuide();
  }, [regions]);

  // Scroll to preview image (center) after capture
  useEffect(() => {
    if (capturedRegions && previewImageRef.current) {
      setTimeout(() => {
        previewImageRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'center'
        });
      }, 300);
    }
  }, [capturedRegions]);

  const captureImage = async (source) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    
    if (source === 'camera' && videoRef.current && containerRef.current) {
      const video = videoRef.current;
      const container = containerRef.current;
      
      // ابعاد واقعی video
      const videoW = video.videoWidth;
      const videoH = video.videoHeight;
      
      // ابعاد container (آنچه کاربر می‌بیند)
      const containerW = container.offsetWidth;
      const containerH = container.offsetHeight;
      
      // نسبت‌های تصویر
      const videoRatio = videoW / videoH;
      const containerRatio = containerW / containerH;
      
      // محاسبه ناحیه قابل مشاهده (object-cover logic)
      let srcX = 0, srcY = 0, srcW = videoW, srcH = videoH;
      
      if (videoRatio > containerRatio) {
        // video پهن‌تر است - از چپ و راست crop میشه
        srcW = videoH * containerRatio;
        srcX = (videoW - srcW) / 2;
      } else {
        // video بلندتر است - از بالا و پایین crop میشه
        srcH = videoW / containerRatio;
        srcY = (videoH - srcH) / 2;
      }
      
      // canvas را به اندازه ناحیه قابل مشاهده تنظیم کن
      canvas.width = srcW;
      canvas.height = srcH;
      
      // فقط ناحیه قابل مشاهده را رسم کن
      ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
      
    } else if (source === 'upload' && uploadedImageRef.current) {
      canvas.width = uploadedImageRef.current.naturalWidth;
      canvas.height = uploadedImageRef.current.naturalHeight;
      ctx.drawImage(uploadedImageRef.current, 0, 0);
    }
    
    // ذخیره تصویر کامل
    const fullImage = canvas.toDataURL('image/jpeg', 1);
    
    const captured = {};
    for (const [name, region] of Object.entries(regions)) {
      const x = region.x * canvas.width, y = region.y * canvas.height, w = region.w * canvas.width, h = region.h * canvas.height;
      const regionCanvas = document.createElement('canvas');
      regionCanvas.width = w;
      regionCanvas.height = h;
      regionCanvas.getContext('2d').drawImage(canvas, x, y, w, h, 0, 0, w, h);
      captured[name] = regionCanvas.toDataURL('image/jpeg', 1);
    }
    return { captured, fullImage };
  };

  const handleCapture = async () => {
    setStatus({ type: 'info', message: lang === 'fa' ? 'در حال عکس‌برداری...' : 'Capturing...' });
    const result = await captureImage(uploadMode ? 'upload' : 'camera');
    if (result) {
      setCapturedRegions(result.captured);
      setCapturedFullImage(result.fullImage);
      
      // بعد از capture، حالت آپلود رو ببند و دوربین رو فعال کن
      if (uploadMode) {
        setUploadMode(false);
        setUploadedImageSrc(null);
      }
      
      setStatus({ type: 'success', message: lang === 'fa' ? 'تصویر با موفقیت ثبت شد!' : 'Image captured successfully!' });
    }
  };

  const handleCloseUpload = () => {
    setUploadMode(false);
    setUploadedImageSrc(null);
    setStatus({ type: 'info', message: lang === 'fa' ? 'دوربین فعال شد' : 'Camera activated' });
  };

  const handleUpload = () => fileInputRef.current?.click();

  // 👇 تغییر اصلی: موقع انتخاب فایل، مدال cropper باز میشه
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setStatus({ type: 'info', message: lang === 'fa' ? 'در حال پردازش تصویر...' : 'Processing image...' });
    
    const reader = new FileReader();
    reader.onload = (event) => {
      // به جای مستقیم نمایش دادن، اول cropper رو باز می‌کنیم
      setTempImageForCrop(event.target.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    
    // Reset input value برای اینکه بتونیم همون فایل رو دوباره انتخاب کنیم
    e.target.value = '';
  };

  // 👇 وقتی کاربر crop رو تأیید کرد
  const handleCropComplete = (croppedImageUrl) => {
    setShowCropper(false);
    setTempImageForCrop(null);
    setUploadedImageSrc(croppedImageUrl);
    setUploadMode(true);
    setStatus({ 
      type: 'success', 
      message: lang === 'fa' 
        ? 'تصویر بارگذاری شد. در صورت نیاز حاشیه‌ها را تنظیم کنید، سپس عکس بگیرید.' 
        : 'Image uploaded. Adjust borders if needed, then capture.' 
    });
  };

  // 👇 وقتی کاربر cropper رو بست
  const handleCropperClose = () => {
    setShowCropper(false);
    setTempImageForCrop(null);
    setStatus({ type: 'info', message: lang === 'fa' ? 'آپلود لغو شد' : 'Upload cancelled' });
  };

  const handleSubmit = async () => {
    if (!capturedRegions) {
      setStatus({ type: 'error', message: lang === 'fa' ? 'ناحیه‌ای ثبت نشده است. ابتدا عکس بگیرید!' : 'No captured regions. Click Capture first!' });
      return;
    }
    
    if (!sessionId) {
      setStatus({ type: 'error', message: lang === 'fa' ? 'شناسه جلسه موجود نیست. لطفاً تأیید هویت را از نو شروع کنید.' : 'No session ID. Please restart verification.' });
      console.error('[SUBMIT] Missing session ID');
      return;
    }
    
    setIsSubmitting(true);
    setStatus({ type: 'info', message: lang === 'fa' ? 'در حال ارسال...' : 'Submitting...' });
    
    console.log('[SUBMIT] Starting submission with session:', sessionId);
    
    try {
      const endpoint = mode === 'automatic' ? 'verify_passport' : 'verify_card';
      const backendUrl = `https://clankless-inflatedly-danae.ngrok-free.dev/${endpoint}`;
      const proxyUrl = `/${endpoint}`;
      
      // Build payload based on mode
      const payload = {
        photo: capturedRegions.photo,
        document: capturedRegions.document,
        raw: capturedFullImage,  // تصویر کامل پاسپورت بدون برش
        session_id: sessionId
      };
      if (mode === 'automatic') {
        payload.mrz = capturedRegions.mrz;
      }
      
      let response;
      try {
        response = await fetch(backendUrl, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            "x-api-key": API_KEY
          },
          body: JSON.stringify(payload)
        });
      } catch (err) {
        console.log('[SUBMIT] Direct backend failed, trying proxy:', err.message);
        response = await fetch(proxyUrl, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            "x-api-key": API_KEY
          },
          body: JSON.stringify(payload)
        });
      }
      
      console.log('[SUBMIT] Response status:', response.status);
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('[SUBMIT] Non-JSON response:', text.substring(0, 200));
        throw new Error(lang === 'fa' ? 'سرور به جای JSON، HTML برگرداند. ممکن است بک‌اند روی پورت 8080 در حال اجرا نباشد.' : 'Server returned HTML instead of JSON. Backend might not be running on port 8080.');
      }
      
      const result = await response.json();
      console.log('[SUBMIT] Response data:', result);
      
      if (result.status === 'SUCCESS') {
        setStatus({ type: 'success', message: lang === 'fa' ? 'تأیید هویت موفق!' : 'Verification successful!' });
        console.log('[SUBMIT] Files saved:', result.saved_paths);
        if (onComplete) setTimeout(() => onComplete(result), 1500);
      } else {
        console.error('[SUBMIT] Verification failed:', result);
        setStatus({ type: 'error', message: `${result.reason || (lang === 'fa' ? 'ناموفق' : 'Failed')}: ${result.message || ''}` });
      }
    } catch (err) {
      console.error('[SUBMIT] Error:', err);
      setStatus({ type: 'error', message: lang === 'fa' ? `خطای شبکه: ${err.message}` : `Network error: ${err.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateRegion = (regionName, updates) => {
    setRegions(prev => ({ ...prev, [regionName]: { ...prev[regionName], ...updates } }));
  };

  const resetBorders = () => {
    setRegions(getInitialRegions());
    setStatus({ type: 'info', message: lang === 'fa' ? 'حاشیه‌ها به حالت پیش‌فرض بازنشانی شدند' : 'Borders reset to default positions' });
  };

  return (
    <Container>
      {/* 👇 مدال Cropper */}
      {showCropper && tempImageForCrop && (
        <ImageCropper
          imageSrc={tempImageForCrop}
          onClose={handleCropperClose}
          onComplete={handleCropComplete}
          lang={lang}
          aspectRatio={1/1}
        />
      )}

      {/* Header Section with Step Indicator */}
      <Card variant="elevated" className="mb-6 p-3">
        <div className="flex items-start justify-between mb-4 flex-col">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-[38px] h-[35px] rounded-full bg-accent-green/20 flex items-center justify-center">
                <CreditCard className="text-accent-green" size={20} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-text-light">
                  {lang === 'fa' ? 'تأیید سند گذرنامه' : 'Passport Document Verification'}
                </h1>
                <p className="text-sm text-text-gray mt-1">
                  {lang === 'fa' ? `مرحله 2 از ${mode === 'automatic' ? '4' : '3'} - فرآیند تایید هویت` : `Step 2 of ${mode === 'automatic' ? '4' : '3'} - Identity Verification Process`}
                </p>
              </div>
            </div>
          </div>
          
          {/* Step Progress Indicator */}
          <div className="flex gap-2">
            <div className="w-12 h-1 bg-accent-green rounded-full"></div>
            <div className="w-12 h-1 bg-accent-green rounded-full"></div>
            <div className="w-12 h-1 bg-accent-green/20 rounded-full"></div>
            {mode === 'automatic' && <div className="w-12 h-1 bg-accent-green/20 rounded-full"></div>}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-accent-green/10 border-2 border-accent-green/30 rounded-xl p-4 flex gap-3">
          <Info className="text-accent-green flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-text-light">
            <p className="font-semibold mb-1">
              {lang === 'fa' ? 'نحوه عکس‌برداری از گذرنامه:' : 'How to capture your passport:'}
            </p>
            <ul className="text-text-gray space-y-1 mt-2 list-disc list-inside">
              <li>{lang === 'fa' ? 'گذرنامه خود را روی سطحی صاف و با نور کافی قرار دهید' : 'Place your passport on a flat, well-lit surface'}</li>
              <li>{lang === 'fa' ? 'اطمینان حاصل کنید که تمام متن‌ها به وضوح قابل مشاهده و تار نباشند' : 'Ensure all text is clearly visible and not blurry'}</li>
              <li>{lang === 'fa' ? 'جعبه‌های رنگی نشان می‌دهند کدام نواحی اسکن خواهند شد' : 'The colored boxes show which areas will be scanned'}</li>
              <li>{lang === 'fa' ? 'در صورت نیاز می‌توانید با استفاده از "تغییر حاشیه‌ها" نواحی اسکن را تنظیم کنید' : 'You can adjust the scan areas using "Modify Borders" if needed'}</li>
            </ul>
          </div>
        </div>

        {sessionId && (
          <div className="mt-4 pt-4 border-t border-accent-green/20">
            <p className="text-xs text-text-gray">
              {lang === 'fa' ? 'شناسه کاربر:' : 'User ID:'} <span className="text-accent-golden font-mono">{userId?.slice(0, 16)}...</span>
            </p>
          </div>
        )}
      </Card>

      {/* Status Display */}
      {status.message && (
        <div className="mb-6">
          <StatusBadge type={status.type} showIcon={true}>
            {status.message}
          </StatusBadge>
        </div>
      )}

      {/* Camera/Upload Preview */}
      <Card variant="elevated" className="mb-6">
        <div 
          ref={containerRef} 
          className="relative mx-auto max-w-2xl bg-primary-black rounded-2xl overflow-hidden border-2 border-accent-green/30 shadow-2xl"
          style={{ 
            aspectRatio: '1/1',
            touchAction: modifyMode ? 'none' : 'auto', // جلوگیری از scroll وقتی در حال modify هستیم
            userSelect: modifyMode ? 'none' : 'auto',
          }}
          onTouchMove={(e) => {
            // جلوگیری از scroll صفحه وقتی روی container در حال درگ هستیم
            if (modifyMode) {
              e.preventDefault();
            }
          }}
        >
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline 
            className="w-full h-full object-cover"
            style={{ display: uploadMode ? 'none' : 'block' }}
          />
          {uploadMode && uploadedImageSrc && (
            <>
              <img 
                ref={uploadedImageRef} 
                src={uploadedImageSrc} 
                alt="Uploaded passport" 
                className="w-full h-full object-contain"
              />
              {/* دکمه بستن عکس آپلود شده */}
              <button
                onClick={handleCloseUpload}
                className="absolute top-3 right-3 w-8 h-8 bg-green-500/20 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors z-10"
                title={lang === 'fa' ? 'بستن و بازگشت به دوربین' : 'Close and return to camera'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </>
          )}
          <canvas 
            ref={overlayRef} 
            className="absolute top-0 left-0 w-full h-full pointer-events-none"
          />
          <DraggableBox 
            id="photoBox" 
            label={regions.photo.label}
            color={regions.photo.color} 
            region={regions.photo} 
            containerRef={containerRef} 
            onUpdate={(updates) => updateRegion('photo', updates)} 
            visible={true} 
            enabled={modifyMode}
            lang={lang}
          />
          {mode === 'automatic' && regions.mrz && (
            <DraggableBox 
              id="mrzBox" 
              label={regions.mrz.label}
              color={regions.mrz.color} 
              region={regions.mrz} 
              containerRef={containerRef} 
              onUpdate={(updates) => updateRegion('mrz', updates)} 
              visible={true} 
              enabled={modifyMode}
              lang={lang}
            />
          )}
          <DraggableBox 
            id="documentBox" 
            label={regions.document.label}
            color={regions.document.color} 
            region={regions.document} 
            containerRef={containerRef} 
            onUpdate={(updates) => updateRegion('document', updates)} 
            visible={true} 
            enabled={modifyMode}
            lang={lang}
          />
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Button onClick={handleCapture} variant="primary" size="sm" fullWidth>
          <Camera className={`inline ${lang==="fa"?" ml-2 ":"mr-2"}`} size={18} />
          {lang === 'fa' ? 'عکس بگیرید' : 'Capture'}
        </Button>
        
        <Button onClick={handleUpload} variant="primary" size="sm" fullWidth>
          <Upload className={`inline ${lang==="fa"?" ml-2 ":"mr-2"}`} size={18} />
          {lang === 'fa' ? 'بارگذاری' : 'Upload'}
        </Button>
        
        <Button 
          onClick={() => setModifyMode(!modifyMode)} 
          variant={modifyMode ? "secondary" : "outline"}
          size="xs" 
          fullWidth
          className='text-xs'
        >
          {modifyMode ? (
            <>
              <Lock className={`inline ${lang==="fa"?" ml-2 ":"mr-2"}`} size={18} />
              {lang === 'fa' ? 'قفل حاشیه‌ها' : 'Lock Borders'}
            </>
          ) : (
            <>
              <Unlock className={`inline ${lang==="fa"?" ml-2 ":"mr-2"}`} size={18} />
              {lang === 'fa' ? 'تغییر حاشیه‌ها' : 'Modify Borders'}
            </>
          )}
        </Button>
        
        <Button onClick={resetBorders} variant="outline" size="sm" fullWidth>
          <RotateCcw className={`inline ${lang==="fa"?" ml-2 ":"mr-2"}`} size={18} />
          {lang === 'fa' ? 'بازنشانی' : 'Reset'}
        </Button>
      </div>

      {/* Scan Area Legend */}
      <Card className="mb-6 p-5">
        <h3 className="text-sm font-semibold text-text-gray uppercase tracking-wider mb-3 flex items-center gap-2">
          <Info size={16} />
          {lang === 'fa' ? 'نواحی اسکن' : 'Scan Areas'}
        </h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded border-2 border-red-500 bg-red-500/20"></div>
            <div>
              <p className="text-sm font-medium text-text-light">
                {lang === 'fa' ? 'ناحیه عکس' : 'Photo Area'}
              </p>
              <p className="text-xs text-text-gray">
                {lang === 'fa' ? 'عکس گذرنامه شما از این ناحیه استخراج خواهد شد' : 'Your passport photo will be extracted from this area'}
              </p>
            </div>
          </div>
          {mode === 'automatic' && (
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded border-2 border-blue-500 bg-blue-500/20"></div>
              <div>
                <p className="text-sm font-medium text-text-light">
                  {lang === 'fa' ? 'ناحیه MRZ' : 'MRZ Area'}
                </p>
                <p className="text-xs text-text-gray">
                  {lang === 'fa' ? 'ناحیه قابل خواندن توسط ماشین - شامل داده‌های رمزگذاری شده گذرنامه' : 'Machine Readable Zone - contains encoded passport data'}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded border-2 border-accent-green bg-accent-green/20"></div>
            <div>
              <p className="text-sm font-medium text-text-light">
                {lang === 'fa' ? 'ناحیه مدرک' : 'Document Area'}
              </p>
              <p className="text-xs text-text-gray">
                {lang === 'fa' ? 'اطلاعات شخصی و جزئیات مدرک' : 'Personal information and document details'}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Preview Section - تصویر با clip-path */}
      {capturedRegions && capturedFullImage && (
        <Card className="p-3 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="text-accent-green" size={20} />
            <h3 className="font-semibold text-text-light">
              {lang === 'fa' ? 'پیش‌نمایش نواحی ثبت شده' : 'Captured Regions Preview'}
            </h3>
          </div>
          
          {/* تصویر کامل با clip-path برای نمایش فقط داخل بردرها */}
          <div ref={previewImageRef} className="relative mx-auto max-w-2xl bg-secondary-black/50 rounded-xl  border-2 border-accent-green/30 mb-6">
            <div className="relative" style={{ aspectRatio: '1/1' }}>
              {/* تصویر پس‌زمینه (خاکستری) */}
              <div className="absolute inset-0 bg-primary-black/80 rounded-lg"></div>
              
              {/* تصویر اصلی با clip-path برای هر ناحیه */}
              {Object.entries(regions).map(([name, region]) => {
                const clipPath = `polygon(
                  ${region.x * 100}% ${region.y * 100}%,
                  ${(region.x + region.w) * 100}% ${region.y * 100}%,
                  ${(region.x + region.w) * 100}% ${(region.y + region.h) * 100}%,
                  ${region.x * 100}% ${(region.y + region.h) * 100}%
                )`;

                return (
                  <div
                    key={name}
                    className="absolute inset-0"
                    style={{
                      clipPath: clipPath,
                      WebkitClipPath: clipPath
                    }}
                  >
                    <img 
                      src={capturedFullImage} 
                      alt={`${name} region`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                );
              })}
              
              {/* بردرها روی تصویر */}
              {Object.entries(regions).map(([name, region]) => {
                const getBorderColor = () => {
                  if (name === 'photo') return 'rgba(220, 38, 38, 1)';
                  if (name === 'mrz') return 'rgba(59, 130, 246, 1)';
                  if (name === 'document') return 'rgba(0, 138, 94, 1)';
                  return 'rgba(255, 255, 255, 1)';
                };

                const getLabel = () => {
                  if (name === 'photo') return lang === 'fa' ? 'عکس' : 'Photo';
                  if (name === 'mrz') return 'MRZ';
                  if (name === 'document') return lang === 'fa' ? 'مدرک' : 'Document';
                  return name;
                };

                return (
                  <div
                    key={`border-${name}`}
                    style={{
                      position: 'absolute',
                      left: `${region.x * 100}%`,
                      top: `${region.y * 100}%`,
                      width: `${region.w * 100}%`,
                      height: `${region.h * 100}%`,
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: '4px',
                      pointerEvents: 'none'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '6px',
                        left: '6px',
                        padding: '3px 8px',
                        backgroundColor: getBorderColor(),
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        borderRadius: '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {getLabel()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Submit Button */}
          <Button 
            onClick={handleSubmit} 
            disabled={!capturedRegions || isSubmitting}
            variant="primary"
            size="lg" 
            fullWidth
          >
            {isSubmitting ? (
              <>
                <Loader2 className={`inline animate-spin ${lang==="fa"?" ml-2 ":"mr-2"}`} size={20} />
                {lang === 'fa' ? 'در حال ارسال...' : 'Submitting...'}
              </>
            ) : (
              <>
                <Send className={`inline ${lang==="fa"?" ml-2 ":"mr-2"}`} size={20} />
                {lang === 'fa' ? 'ارسال تأیید هویت' : 'Submit Verification'}
              </>
            )}
          </Button>
        </Card>
      )}

      {/* Hidden Elements */}
      <input 
        ref={fileInputRef} 
        type="file" 
        accept="image/*" 
        className="hidden" 
        onChange={handleFileChange} 
      />
      <canvas ref={canvasRef} className="hidden" />
    </Container>
  );
}

export default PassportCapture;